<template>
    <!-- Sections sit inside the page's #mfaAccordion; the page keeps which one is open -->

    <form-errors :errors="formErrors"/>

    <p v-if="! hasSections" class="mt-3" data-test="mfa-no-methods">
        This browser can't use your sign-in methods. Try another browser, or use a backup code.
    </p>

    <!-- Section: Trusted Device -->
    <section class="border-top py-2 mt-3" v-if="methods.includes('device') && ! showDeviceSelector" data-test="mfa-device">
        <section-toggle controls="mfaRegDevice" :open="isOpen('device')" @toggle="toggle('device')">
            Use Your Trusted Device
        </section-toggle>
        <div id="mfaRegDevice" v-show="isOpen('device')">
            <div class="pt-1">
                <p>
                    We sent a sign-in prompt to <strong>{{ pushedDeviceLabel }}</strong>.
                    <template v-if="currentPushNumber">Open it, then tap the number below.</template>
                    <template v-else>Open it, then tap <strong>Allow Sign In</strong>.</template>
                </p>

                <div v-if="currentPushNumber" class="push-number" data-test="push-number">
                    <div class="push-number-label">Your number</div>
                    <div class="push-number-value">{{ currentPushNumber }}</div>
                </div>
                <div v-else style="max-width: 8rem; margin: 0 auto; ">
                    <img class="img-fluid" src="@/assets/img/app-open.gif" alt="">
                </div>

                <p class="text-center text-muted mt-3 mb-1">
                    <i aria-hidden="true" class="fas fa-circle-notch fa-spin text-primary mr-1"></i>
                    Waiting for you to approve…
                </p>
                <p v-if="currentPushNumber" class="text-center text-muted small mb-0">
                    Didn't try to sign in? Tap <strong>It wasn't me</strong> on your phone.
                </p>
            </div>

            <div v-if="mobileDevices.length > 1" class="mt-3 mb-2">
                <button type="button" class="btn btn-outline-secondary btn-block btn-different-device" @click="selectedTrustedDevice = false">
                    <i aria-hidden="true" class="fal fa-mobile mr-2"></i>Use a different device
                </button>
            </div>
        </div>
    </section>

    <!-- Section: Select Trusted Device -->
    <section class="border-top py-2 mt-3" v-if="methods.includes('device') && showDeviceSelector" data-test="mfa-device">
        <section-toggle controls="mfaSelectDevice" :open="isOpen('device')" @toggle="toggle('device')">
            Use A Trusted Device
        </section-toggle>
        <div id="mfaSelectDevice" v-show="isOpen('device')">
            <div class="pt-1">
                <p>
                    Select a Trusted Device to send a prompt to, then follow the prompt in the GetMyPayStub app on that device.
                </p>

                <div class="row">
                    <div class="col-md-4 mb-3" v-for="device in mobileDevices">
                        <div class="card card-body text-center tile-button h-100" @click.prevent="sendDevicePush(device)">
                            <div class="mb-1">
                                <i :class="['fal', device.icon, 'display-4', 'opacity-50']"></i>
                            </div>

                            <div class="mb-1">
                                {{ device.label }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Section: Passkey -->
    <section class="border-top py-2 mt-3" v-if="methods.includes('passkey') && passkeySupported" data-test="mfa-passkey">
        <section-toggle controls="mfaPasskey" :open="isOpen('passkey')" @toggle="toggle('passkey')">
            Use a passkey
        </section-toggle>
        <div id="mfaPasskey" v-show="isOpen('passkey')">
            <div class="pt-1">
                <p>Use your fingerprint, face, screen lock or security key.</p>
                <button type="button" class="btn btn-primary" :disabled="passkeyBusy" @click="usePasskey">Use a passkey</button>
            </div>
        </div>
    </section>

    <!-- Section: Authenticator or backup code -->
    <section class="border-top py-2 mt-3" v-if="hasCodeSection" data-test="mfa-code">
        <section-toggle controls="mfaCode" :open="isOpen('code')" @toggle="toggle('code')">
            Use an authenticator app or backup code
        </section-toggle>
        <div id="mfaCode" v-show="isOpen('code')">
            <form @submit.prevent="onSubmit" novalidate class="mui-form">
                <div class="form-row">
                    <div class="col-12 col-sm-8">
                        <!-- Plain input: form-input overrides autocomplete, and one-time-code is the point here -->
                        <div :class="{'has-danger': fieldErrors.length}">
                            <div class="mui-textfield mui-textfield-flipped">
                                <input id="mfa-code" name="mfa_code" type="text" maxlength="11" autocomplete="one-time-code" autocapitalize="off" spellcheck="false" v-model="formData.mfa_code" :class="{'mui--is-invalid': fieldErrors.length}">
                                <label for="mfa-code">Code from your authenticator app, or a backup code</label>
                            </div>
                            <div class="field-helper-text text-danger" v-if="fieldErrors.length">
                                <i aria-hidden="true" title="Error" class="fas fa-fw fa-exclamation-circle"></i>
                                <span class="sr-only">Error:</span>
                                <span> {{ fieldErrors[0] }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="pt-2">
                    <button type="submit" class="btn btn-primary mr-2 mb-1">Sign In</button>
                </div>
            </form>
        </div>
    </section>
</template>

<script>
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser'
import FormMixin from '@/mixins/Form'
import SectionToggle from '@/components/Login/SectionToggle'
import api from '@/api'
import bus from '@/bus'

export default {
    mixins: [FormMixin],
    components: {SectionToggle},
    props: {
        methods: {type: Array, required: true},
        mobileDevices: {type: Array, default: () => []},
        devicePushSent: {type: Boolean, default: false},
        // Shown so the user can pick it on the phone
        pushNumber: {type: String, default: ''},
        email: {type: String, default: ''},
        // The page's completion: /login/complete, then on to the next page
        complete: {type: Function, required: true},
        // The page's open accordion section ('device', 'passkey', 'code', or its own)
        openSection: {type: String, default: ''},
    },
    emits: ['update:openSection'],
    data() {
        return {
            checkStatusTask: null,
            checkStatusEnabled: true,
            // A single phone has already been pushed to
            selectedTrustedDevice: this.devicePushSent,
            // The server auto-pushes only when there is one phone
            pushedDevice: this.mobileDevices.length == 1 ? this.mobileDevices[0] : null,
            currentPushNumber: this.pushNumber,
            passkeySupported: browserSupportsWebAuthn(),
            passkeyBusy: false,
            formRules: {
                mfa_code: {
                    presence: {allowEmpty: false},
                },
            },
            formData: {
                mfa_code: '',
            },
        }
    },
    computed: {
        hasCodeSection() {
            return this.methods.includes('totp') || this.methods.includes('backup_code')
        },
        // In page order: the phone first, since a push may already be waiting
        sections() {
            return [
                this.methods.includes('device') && 'device',
                this.methods.includes('passkey') && this.passkeySupported && 'passkey',
                this.hasCodeSection && 'code',
            ].filter(Boolean)
        },
        hasSections() {
            return this.sections.length > 0
        },
        pushedDeviceLabel() {
            return (this.pushedDevice && this.pushedDevice.label) || 'your Trusted Device'
        },
        fieldErrors() {
            return (this.errors && this.errors.mfa_code) || []
        },
        showDeviceSelector() {
            return this.mobileDevices.length > 1 && !this.selectedTrustedDevice
        },
    },
    created() {
        if (!this.openSection && this.sections.length) {
            this.$emit('update:openSection', this.sections[0])
        }
    },
    mounted() {
        this.startCheckStatus()
    },
    beforeUnmount() {
        this.checkStatusEnabled = false
        clearTimeout(this.checkStatusTask)
        this.checkStatusTask = null
    },
    methods: {
        isOpen(section) {
            return this.openSection == section
        },
        toggle(section) {
            this.$emit('update:openSection', this.isOpen(section) ? '' : section)
        },
        // A dead attempt (too many tries, too weak, expired) ends back at login
        restart(errors, logout) {
            if (logout) {
                this.$store.dispatch('LOGOUT')
            }
            this.$router.push({name: 'login'})
            bus.showError(bus.firstError(errors))
        },
        onFailure(errors) {
            const status = errors.__status__
            if (status == 429 || status == 401) {
                this.restart(errors, true)
            } else if (status == 404) {
                this.restart(errors, false)
            } else {
                // 400 is a typo or a failed assertion - let them try again
                this.errors = errors
                this.formErrors = errors.__all__ || []
            }
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            this.formErrors = []
            this.complete({mfa_code: this.formData.mfa_code}).catch(this.onFailure)
        },
        usePasskey() {
            if (this.passkeyBusy) {
                return
            }
            this.passkeyBusy = true
            this.formErrors = []
            api.post('/login/passkey/begin', {}).then((optionsJSON) => {
                return startAuthentication({optionsJSON})
            }).then((credential) => {
                return api.post('/login/passkey/finish', {credential})
            }).then(() => {
                return this.complete({})
            }).catch((errors) => {
                // Cancelling the browser prompt rejects with a DOMException
                if (errors && errors.__status__) {
                    this.onFailure(errors)
                } else {
                    this.formErrors = ["That passkey didn't work. Try again or use another method."]
                }
            }).then(() => {
                this.passkeyBusy = false
            })
        },
        startCheckStatus() {
            if (!this.methods.includes('device')) {
                return
            }

            if (!this.checkStatusEnabled) {
                return
            }

            if (this.checkStatusTask) {
                return
            }

            this.checkStatusTask = setTimeout(() => {
                this.checkStatus()
            }, 1000)
        },
        checkStatus() {
            const challengeKey = this.$store.state.session?.login_attempt_challenge_key
            if (!challengeKey) return
            // don't extend (or re-save) the session: a poll in flight as the
            // login completes would otherwise hit the session it replaced
            api.get(`/login/attempt/${challengeKey}`, true).then(resp => {
                if (resp.status == 'validated') {
                    this.complete({}).catch(errors => {
                        bus.showError(errors.__all__)
                    })
                }

                if (resp.status == 'pending') {
                    this.checkStatusTask = null
                    this.startCheckStatus()
                }

                // "It wasn't me" on the phone
                if (resp.status == 'denied') {
                    this.checkStatusEnabled = false
                    this.restart({__all__: ['This sign-in was blocked from your trusted device.']}, true)
                }

                // spent: a wrong number picked on the phone, or too many wrong codes
                if (resp.status == 'expired') {
                    this.checkStatusEnabled = false
                    this.restart({__all__: ['That sign-in was blocked. Please start again.']}, true)
                }
            }).catch(errors => {
                // TODO: Check errors.__status__
                bus.showError(errors.__all__)
            })
        },
        sendDevicePush(device) {
            this.checkStatusEnabled = true
            this.$store.dispatch('START_LOADING')
            api.post('/login/device-push', {'device_id': device.id}).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.currentPushNumber = (resp && resp.push_number) || ''
                bus.showInfo('Sent!')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.checkStatusEnabled = false
                this.$store.dispatch('LOGOUT')
                this.$router.push({name: 'login'})
                bus.showError(bus.firstError(errors))
            })
            this.selectedTrustedDevice = true
            this.pushedDevice = device
        },
    },
}
</script>

<style scoped>
.push-number {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
}
.push-number-label {
    font-size: 0.8125rem;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #8A3300;
}
.push-number-value {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 8.75rem;
    height: 8.75rem;
    border: 3px solid #FE4E00;
    border-radius: 50%;
    font-size: 4rem;
    font-weight: 700;
    line-height: 1;
    color: #222222;
    font-variant-numeric: tabular-nums;
}
.btn-different-device {
    min-height: 3rem;
}
</style>
